import { ActionKeys } from '~/actions/constants';
import { showDialog } from '~/actions/dialog.actions';
import * as pickupsActions from '~/actions/pickups.actions';
import { AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import * as pickupsService from '~/services/pickups.service';
import { JSONCarrierAccount } from '~/types/CarrierAccount';
import CarrierBuildingLocation from '~/types/CarrierBuildingLocation';
import { JSONRate } from '~/types/Rate';

export function* getTransactions(
  action: ReturnType<typeof pickupsActions.getTransactionsForCarrier>,
) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: AxiosResponse<{
      count: number;
      totals: { total_weight: string };
      results: { object_id: string; rate: JSONRate }[];
    }> = yield call(
      pickupsService.getTransactions,
      action.payload.carrier,
      action.payload.startDate,
      action.payload.endDate,
    );

    const totalTransactions = result.data.count;
    const transactionObjectIds: string[] = result.data.results.map(
      (transaction: { object_id: string }) => transaction.object_id,
    );

    const serviceLevelInfo = result.data.results.reduce<Record<string, number>>(
      (obj, transaction) => {
        const serviceLevelName = transaction.rate.servicelevel_name;
        if (obj[serviceLevelName as string]) {
          return { ...obj, [serviceLevelName as string]: obj[serviceLevelName as string] + 1 };
        }

        return { ...obj, [serviceLevelName as string]: 1 };
      },
      {},
    );

    const payload = {
      totalTransactions,
      transactionObjectIds,
      serviceLevelInfo,
      // TODO: This rounding is for presentational purposes and should happen in the view
      totalWeight: Math.round(100 * Number(result.data.totals.total_weight)) / 100,
    };

    yield put(pickupsActions.getTransactionsForCarrierSuccess(payload));
  } catch (err) {
    const errorMessage =
      'There is an error from the carrier that requires investigation. Please contact support.';
    const payload = {
      messages: [errorMessage],
      closeButtonText: 'Close',
    };
    yield put(pickupsActions.getTransactionsForCarrierError(errorMessage));
    yield put(showDialog(payload, 'error'));
  }
}

export function* getCarrierAccountId(
  action: ReturnType<typeof pickupsActions.getCarrierAccountId>,
) {
  try {
    const result: AxiosResponse<{ results: JSONCarrierAccount[] }> = yield call(
      pickupsService.getCarrierAccounts,
      action.payload.carrier,
    );

    let carrierAccountId: string | undefined | null = null;
    for (const carrier of result.data.results) {
      if (carrier.is_shippo_account) {
        carrierAccountId = carrier.object_id;
      }
    }

    const payload = {
      carrierAccountId,
    };
    yield put(pickupsActions.getCarrierAccountIdSuccess(payload));
  } catch (err) {
    const errorMessage =
      'There is an error from the carrier that requires investigation. Please contact support.';
    const payload = {
      messages: [errorMessage],
      closeButtonText: 'Close',
    };
    yield put(pickupsActions.getCarrierAccountIdError(errorMessage));
    yield put(showDialog(payload, 'error'));
  }
}

export function* getCarrierBuildingLocationTypes(
  action: ReturnType<typeof pickupsActions.getCarrierBuildingLocationTypes>,
) {
  try {
    const result: AxiosResponse<CarrierBuildingLocation[]> = yield call(
      pickupsService.getCarrierBuildingLocationTypes,
      action.payload.carrier,
    );

    const payload = result.data;
    yield put(pickupsActions.getCarrierBuildingLocationTypesSuccess(payload));
  } catch (err) {
    const errorMessage =
      'There is an error from the carrier that requires investigation. Please contact support.';
    const payload = {
      messages: [errorMessage],
      closeButtonText: 'Close',
    };
    yield put(pickupsActions.getCarrierBuildingLocationTypesError(errorMessage));
    yield put(showDialog(payload, 'error'));
  }
}

export function* sendPickupsRequest(action: ReturnType<typeof pickupsActions.sendPickupsRequest>) {
  try {
    // TODO: Type this and add toJSON function
    const country = action.payload.address.country ? action.payload.address.country.iso2 : 'US';
    const requestPayload = {
      carrier_account: action.payload.carrierAccountId,
      location: {
        instructions: action.payload.buildingLocationType.label,
        building_type: '',
        address: {
          country,
          name: action.payload.address.name,
          company: action.payload.address.company,
          street1: action.payload.address.street1,
          street2: action.payload.address.street2,
          city: action.payload.address.city,
          state: action.payload.address.state,
          zip: action.payload.address.zip,
          phone: action.payload.address.phone,
          email: action.payload.address.email,
        },
        building_location_type: action.payload.buildingLocationType.label,
      },
      transactions: action.payload.transactionObjectIds,
      requested_start_time: action.payload.startTime,
      requested_end_time: action.payload.endTime,
      metadata: '',
      timezone: action.payload.timeZone,
      is_test: ENV_ID !== 'production',
    };

    const result: AxiosResponse<{
      confirmation_code: string;
      confirmed_end_time: string;
      carrier: string;
      confirmed_start_time: string;
      timezone: string;
    }> = yield call(pickupsService.sendPickupsRequest, requestPayload);
    const payload = {
      confirmationCode: result.data.confirmation_code,
      pickupDate: new Date(result.data.confirmed_end_time).toJSON().slice(0, 10),
      carrier: result.data.carrier,
      pickupReadyByTime: result.data.confirmed_start_time,
      timeZone: result.data.timezone,
    };
    yield put(pickupsActions.sendPickupsRequestSuccess(payload));
  } catch (err) {
    let errorMessage =
      'There is an error from the carrier that requires investigation. Please contact support.';
    if (
      err.response &&
      err.response.data &&
      err.response.data.messages &&
      err.response.data.messages.length > 0
    ) {
      errorMessage = err.response.data.messages[0];
    }
    const payload = {
      messages: [errorMessage],
      closeButtonText: 'Close',
    };
    yield put(pickupsActions.sendPickupsRequestError(errorMessage));
    yield put(showDialog(payload, 'error'));
  }
}

export default [
  takeLatest(ActionKeys.PICKUPS_FETCH_TRANSACTIONS, getTransactions),
  takeLatest(ActionKeys.PICKUPS_FETCH_CARRIER_ACCOUNT_ID, getCarrierAccountId),
  takeLatest(
    ActionKeys.PICKUPS_FETCH_CARRIER_BUILDING_LOCATION_TYPES,
    getCarrierBuildingLocationTypes,
  ),
  takeLatest(ActionKeys.PICKUPS_SEND_PICKUPS_REQUEST, sendPickupsRequest),
];
